import type { PlatformFeatureToggles } from "@shared/featureToggles";

import type { Currency } from "./Finance";

/* atoms */

/**
 * A localized text is a record of localeCode -> translation
 * e.g. { en: "Hello", fr: "Bonjour" }
 *
 * If you add a new field of this type, make sure to add it to the `localizedLabelFieldKeys` array in `utils/convertToFrontendPlatformConfiguration.ts`
 */
export type LocalizedText = Record<string, string>;

export enum LogoType {
  PrimaryLogo = "primaryLogo",
  SecondaryLogo = "secondaryLogo",
  LightModeLogo = "lightModeLogo",
}

export enum FooterType {
  MwsFooter = "mwsFooter",
  WhitelabelFooter = "whitelabelFooter",
  FabricksFooter = "fabricksFooter",
}

export type Font = {
  // More properties to define a fontface can be added later
  url: string | null;
  weight: "normal" | "bold" | number | null; // https://developer.mozilla.org/en-US/docs/Web/CSS/@font-face/font-weight#formal_syntax
};

export type Language = {
  isoLanguageCode: string;
};

export type PaymentOption = {
  name?: string;
  imageUri?: string;
};

export type Social = {
  name?: string;
  imageUri?: string;
  qrCodeUri?: string;
  link?: string;
};

export type Theme = {
  "text-1": string;
  "text-2": string;
  "text-3": string;
  "background-1": string;
  "background-2": string;
  "background-3": string;
  "border-1": string;
  "border-2": string;
  "color-1": string;
  "color-2": string;
  "color-3": string;
  "color-4": string;
  "overlay-1": string;
  neutral: string;
  success: string;
  danger: string;
  warning: string;
};

export type BlockStyling = {
  usesGlobalTheme: boolean;
  theme: Partial<Theme> | null;
};

export type FooterProperties = {
  supportEmail?: string;
  availableSocials: Social[];
  paymentOptions: PaymentOption[];
  logo: LogoType;
  isSubfooterShown: boolean;
  styling: BlockStyling;
  newsletterOptions: {
    enabled?: boolean;
    theme: Partial<Theme> | null;
  };
};

export type HeaderProperties = {
  productAuthenticationActive: boolean;
  logo: LogoType;
};

export type PlatformConfigurationStyling = {
  theme: Theme;
  fonts: Font[] | null;
};

export type Footer = {
  type?: FooterType;
  properties: FooterProperties;
};

export type Header = {
  properties: HeaderProperties;
};

export type HeadMeta = {
  description: string;
};

export type Head = {
  meta: HeadMeta;
};

export type Definition = {
  styling: PlatformConfigurationStyling;
  assets: {
    primaryLogo: string;
    secondaryLogo: string | null;
    lightModeLogo: string | null;
    favIconUrl: string | null;
  };
  header: Header;
  footer: Footer;
  configurationDefaultHead: Head;
  properties: {
    defaultLanguage: Language;
    availableLanguages: Language[];
    defaultCurrency: Currency;
    availableCurrencies: Currency[];
    isCategorySearchEnabled: boolean;
    /**
     * Whether or not we mention MatchWornShirt in whitelabel instances
     *
     * e.g. say "Create your PSG account at MatchWornShirt" as opposed to "Create your PSG account"
     */
    referencesMatchWornShirt: boolean;
    /**
     * Whether or not we mention FrameTheGame in whitelabel instances
     *
     * e.g. show the FTG logo on the "frame your shirt" page
     */
    isFrameTheGameOfficialPartner: boolean;
    featureFlags: PlatformFeatureToggles;
  };
};

export type TrackingSettings = {
  isEnabled: boolean;
  segmentWriteKey: string;
  ga4MeasurementId: string;
};

export type PlatformConfiguration = {
  platformId: string;
  platformName: string;
  configurationId: string;
  configurationName: string;
  isAuctionsEnabled: boolean;
  isFabricksEnabled: boolean;
  cookieBotKey?: string | null;
  onlyAutomaticallyPublishFixtureEvents: boolean;
  /**
   * Denotes the primary domain of the platform.
   *
   * For example, this will be `matchwornshirt.com` even though the platform is also accessible from `next.matchwornshirt.com`
   */
  primaryDomain: string;
  versionNumber?: number; // Injected in the Platform Configurator
  isPublished?: boolean; // Injected in the Platform Configurator
  tracking: TrackingSettings;
} & Definition;
